/**
 * @file Public landing page — pages/Landing/LeadershipPage.tsx.
 */

import React from "react";
import { Link } from "react-router-dom";
import "./LandingPage.css";
import "./ContentPage.css";

const LeadershipPage: React.FC = () => {
  return (
    <div className="landing-root">
      <header className="landing-header flex justify-center items-center space-x-3 text-center py-4">
        <h1 className="landing-main-title text-4xl font-bold text-primary">
          ICoMEC
        </h1>
        <h2 className="landing-sub-title text-xl text-grayDark">
          Indian Construction Materials Embodied Carbon Data Base
        </h2>
      </header>

      <nav className="landing-nav">
        <Link to="/">Home</Link>
        <Link to="/join">Join</Link>
        <Link to="/impact">Impact</Link>
        <Link to="/research">Research and Analysis</Link>
        <Link to="/about">About</Link>
      </nav>

      <section className="content-page">
        <h1 className="content-title">Leadership</h1>
        
        <div className="content-section">
          <h2>Domain Expertise & Framework Stewardship</h2>
          <p>
            The ICoMEC platform is overseen by researchers and practitioners specialized in the life cycle assessment of the built environment. Our governance focuses on the systematization of carbon data through the lens of statistics and industrial reality.
          </p>
          <p>Our core leadership brings specialized experience in:</p>
          <ul className="content-list">
            <li><strong>Applied Life Cycle Assessment (LCA):</strong> Translating LCA principles into functional tools for the built environment.</li>
            <li><strong>Research Management:</strong> Coordinating large-scale data acquisition and peer-reviewed validation processes.</li>
            <li><strong>Multilateral Engagement:</strong> Facilitating dialogue between architects, engineers, and LCA-practitioners, to align on climate goals.</li>
          </ul>
        </div>

        <div className="content-section">
          <h2>Accountability and Data Integrity</h2>
          <p>
            The leadership is committed to the continuous improvement of the ICoMEC database. Our work is centered on ensuring that the platform serves as a reliable embodied carbon database for the construction sector.
          </p>
          <p>We prioritize:</p>
          <ul className="content-list">
            <li><strong>Methodological Integrity:</strong> Adherence to rigorous statistical frameworks to maintain the precision and accuracy of results.</li>
            <li><strong>Traceability & Transparency:</strong> Ensuring every data point is fully documented and auditable.</li>
            <li><strong>Long-Term Utility:</strong> Developing a permanent, open-access technical resource that evolves with shifts in manufacturing and energy systems.</li>
          </ul>
        </div>

        <div className="content-section">
          <h2>Cross-Sectoral Validation</h2>
          <p>
            The development of the ICoMEC framework is supported by an advisory network of subject matter experts. This ensures that the platform's outputs are scientifically sound and industrially applicable.
          </p>
          <p>Our advisory collaboration includes:</p>
          <ul className="content-list">
            <li><strong>Academic Researchers:</strong> Providing oversight on LCA methodologies and statistical modeling.</li>
            <li><strong>Industrial Experts:</strong> Validating manufacturing inputs and material supply chain variables.</li>
            <li><strong>Regulatory Institutions:</strong> Ensuring alignment with emerging environmental standards and policy frameworks.</li>
          </ul>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="footer-main">
          <div className="footer-left">
            <h2>ICoMEC</h2>
            <p>
              Indian Construction Materials Embodied
              <br /> Carbon Data Base
            </p>
          </div>

          <div className="footer-links">
            <div>
              <h4>About Us</h4>
              <ul>
                <li><Link to="/who-we-are">Who We Are</Link></li>
                <li><Link to="/what-we-do">What We Do</Link></li>
                <li><Link to="/leadership">Leadership</Link></li>
                <li><Link to="/partners">Partners</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/glossary">Glossary</Link></li>
              </ul>
            </div>
            <div>
              <h4>Resources</h4>
              <ul>
                {/* <li><Link to="/resource-library">Resource Library</Link></li> */}
                <li><Link to="/events">Events</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      <div className="footer-bottom">
        <p>
          <Link to="/cookies-policy">Cookies Policy</Link> · <Link to="/privacy-policy">Privacy Policy</Link> · <Link to="/legal">Legal</Link> · <Link to="/terms-of-use">Terms of Use</Link>
          {/* · <Link to="/imprint">Imprint</Link> */}
        </p>
        <p>© 2026 CARBSE. All rights reserved.</p>
      </div>
    </div>
  );
};

/** React component — Leadership Page. */
export default LeadershipPage;
